import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';

// Components
import Label from '../../components/Label';
import CustomButton from '../../components/CustomButton';
import FormikTextField from '../../components/form/FormikTextField';
import FormikMultiSelect from '../../components/form/FormikMultiSelect';

// Prime-React Components
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';

// Redux-Actions
import {
  addCommonParams,
  editCommonParames,
  getCommonParamsById,
  getFilterCommonParamsList
} from '../../redux/actions/commonParameter';

// Redux-Slices
import { apiStatusClear } from '../../redux/slices/apiStatus';
import { updateData } from '../../redux/slices/commonParamters';

// Constants
import { BUTTON_TYPES, INITIAL_PAGE_NUMBER, LABEL_TYPES, ROUTES } from '../../constants/common';
import { useNavigate } from 'react-router-dom';
import { addEditCommonParameterSchema } from '../../schema/masterData.Schema';
import { getFormErrorMessage } from '../../utils/common';

const AddEditCommonParams = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { show, onHide, ID } = props;
  const { parameterDetails, filterParameters } = useSelector((state) => state.parameter);

  const { isSucceed, isLoading } = useSelector((state) => state.apiStatus);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useState(false);
  const [feedbackParamsOptions, setFeedbackParamsOptions] = useState([]);
  const [parameterData, setParameterData] = useState({
    parameterName: '',
    isGroupParameter: false,
    subParameterIds: []
  });

  const queryParamsOfFeedbackParams = {
    isSingleParameter: true,
    searchValue: '',
    pageNumber: INITIAL_PAGE_NUMBER,
    size: 10000,
    sortBy: 'DateModified',
    orderBy: 'desc'
  };

  const feedbackParamsOptionsGenerate = (options) => {
    if (!options?.length) return [];

    return options
      ?.filter((param) => param.ID !== ID)
      .map((opt) => {
        return {
          name: `${opt.ParameterName}`,
          code: `${opt.ID}`,
          key: `${opt.ID}`
        };
      });
  };

  useEffect(() => {
    const modifiedOptions = feedbackParamsOptionsGenerate(filterParameters || []);
    setFeedbackParamsOptions(modifiedOptions);
  }, [filterParameters]);

  useEffect(() => {
    if (show) {
      dispatch(getFilterCommonParamsList(queryParamsOfFeedbackParams));
    }
  }, [show]);

  useEffect(() => {
    ID && dispatch(getCommonParamsById(ID));
  }, [dispatch, ID]);

  useEffect(() => {
    if (ID && parameterDetails && Object.keys(parameterDetails).length !== 0) {
      setParameterData({
        parameterName: parameterDetails?.ParameterName,
        isGroupParameter: !parameterDetails?.IsSingleParameter,
        subParameterIds: parameterDetails?.GroupParameters?.length
          ? parameterDetails?.GroupParameters.map((param) => param.FeedBackParameterID)
          : []
      });
    }
  }, [parameterDetails]);

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide(false);
      formik.resetForm();
      document.body.classList.remove('sidebar-open');
      dispatch(updateData());
      dispatch(apiStatusClear());
      setParameterData({
        parameterName: '',
        isGroupParameter: false,
        subParameterIds: []
      });
      setIsUpdatedSuccess(false);
      navigate(ROUTES.ADMIN.COMMON_PARAMETERS);
    }
  }, [isSucceed]);

  useEffect(() => () => setIsUpdatedSuccess(false), []);

  const formik = useFormik({
    initialValues: parameterData,
    validationSchema: addEditCommonParameterSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const handleSubmit = (values) => {
    if (ID) {
      dispatch(
        editCommonParames({
          formdata: {
            parameterName: values.parameterName,
            isSingleParameter: !values.isGroupParameter,
            subParameterIds: values.subParameterIds
          },
          ID: ID
        })
      );
    } else {
      dispatch(
        addCommonParams({
          parameterName: values.parameterName,
          isSingleParameter: !values.isGroupParameter,
          subParameterIds: values.subParameterIds
        })
      );
    }
    setIsUpdatedSuccess(true);
  };

  return (
    <Sidebar
      ID={ID}
      visible={show}
      onHide={() => {
        onHide(true);
        document.body.classList.remove('sidebar-open');
      }}
      position="right"
      className="sidebar-drawer">
      <div className="form-box-wrapper">
        <div className="title-wrapper">
          <p className="card-title">{ID ? 'Edit' : 'Add'} Feedback Parameter</p>
          <button
            className="p-sidebar-close"
            onClick={() => {
              onHide(false);
              document.body.classList.remove('sidebar-open');
              setParameterData({
                parameterName: '',
                isGroupParameter: false,
                subParameterIds: []
              });
              formik.resetForm();
            }}>
            <span className="p-sidebar-close-icon pi pi-times" />
          </button>
        </div>
        <form onSubmit={formik.handleSubmit} className="p-fluid" autoComplete="off">
          <div className="form-row-wrapper">
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.PARAMETER}
                    isMandatory
                    formik={formik}
                    name="parameterName"
                    placeholder="Add Parameter *"
                  />
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group horizontal-switch">
                  <Label for="isGroupParameter" text={LABEL_TYPES.IS_GROUPED} />
                  <InputSwitch
                    checked={formik.values.isGroupParameter}
                    onChange={(e) => {
                      formik.setFieldValue('isGroupParameter', e.value);
                      if (!e.value) {
                        formik.setFieldValue('subParameterIds', []);
                      }
                    }}
                  />
                </div>
                {getFormErrorMessage(formik, 'isGroupParameter')}
              </div>
            </div>
            {formik.values.isGroupParameter && (
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group autocomplete">
                    <FormikMultiSelect
                      labelText={LABEL_TYPES.SUB_PARAMETERS}
                      formik={formik}
                      name="subParameterIds"
                      placeholder="Select Feedback Params"
                      optionLabel="name"
                      optionValue="code"
                      options={feedbackParamsOptions}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="form-btn-wrapper">
            <CustomButton type="submit" variant="contained" className="" disabled={isLoading}>
              {BUTTON_TYPES.SAVE}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={() => {
                onHide(false);
                document.body.classList.remove('sidebar-open');
                setParameterData({
                  parameterName: '',
                  isGroupParameter: false,
                  subParameterIds: []
                });
                formik.resetForm();
              }}
              color="error"
              className="gray-btn">
              {BUTTON_TYPES.CANCEL}
            </CustomButton>
          </div>
        </form>
      </div>
    </Sidebar>
  );
};

export default AddEditCommonParams;
